import React from 'react';
import { Routes, Route, Navigate, useSearchParams, useLocation } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import theme from './theme';
import { useAuth } from './context/AuthContext';

// Components
import Home from './components/Home';
import Login from './components/auth/Login';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import SetupPassword from './components/auth/SetupPassword';
import Dashboard from './components/Dashboard';
import VendorsList from './components/VendorsList';
import ExhibitorInfo from './components/ExhibitorInfo';
import Visitors from './components/Visitors';
import Directions from './components/Directions';
import VendorOnboarding from './components/vendor/VendorOnboarding';
import VendorDashboard from './components/vendor/VendorDashboard';
import VendorPaymentPage from './components/vendor/VendorPaymentPage';
import VendorConfirmationPage from './components/vendor/VendorConfirmationPage';
import MapView from './components/MapView';
import AdminLayout from './components/admin/AdminLayout';
import HomePage from './components/admin/pages/HomePage';
import ApprovalsPage from './components/admin/pages/ApprovalsPage';
import VendorsPage from './components/admin/pages/VendorsPage';
import BoothsPage from './components/admin/pages/BoothsPage';
import PaymentsPage from './components/admin/pages/PaymentsPage';
import UsersPage from './components/admin/pages/UsersPage';
import WebhooksPage from './components/admin/pages/WebhooksPage';
import MapEditorPage from './components/admin/pages/MapEditorPage';
import EmailSettingsPage from './components/admin/pages/EmailSettingsPage';
import CategoriesPage from './components/admin/pages/CategoriesPage';
import RegistrationFlowPage from './components/admin/pages/RegistrationFlowPage';
import VendorDetailsPage from './components/admin/VendorDetailsPage';
import PaymentDetailsPage from './components/admin/PaymentDetailsPage';
import FinalRegistrations from './components/admin/FinalRegistrations';
import VendorPrintSheets from './components/admin/VendorPrintSheets';
import FestivalsManager from './components/admin/FestivalsManager';
import {
  LOGIN_REASON_MESSAGES,
  persistLoginBanner,
} from './utils/loginMessages';

const ProtectedRoute = ({ children, requireAdmin = false, requireSuperAdmin = false }) => {
  const { isAuthenticated, loading, isAdmin, isSuperAdmin } = useAuth();
  const location = useLocation();

  if (loading) {
    return null;
  }

  if (!isAuthenticated) {
    const isVendorRoute = location.pathname.startsWith('/vendor');
    if (isVendorRoute) {
      persistLoginBanner(LOGIN_REASON_MESSAGES.vendor_dashboard);
    }
    return (
      <Navigate
        to="/login"
        replace
        state={
          isVendorRoute
            ? { message: LOGIN_REASON_MESSAGES.vendor_dashboard }
            : undefined
        }
      />
    );
  }

  if (requireSuperAdmin && !isSuperAdmin) {
    return <Navigate to="/admin" replace />;
  }

  if (requireAdmin && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Backwards-compatible redirect for old /admin?tab=N bookmarks. Maps legacy
// numeric tab IDs to the equivalent route under the new sidebar shell.
const LEGACY_TAB_ROUTES = {
  '0': '/admin/vendors',
  '1': '/admin/booths',
  '2': '/admin/users',
  '3': '/admin/webhooks',
  '4': '/admin/payments',
  '5': '/admin/approvals?status=pending',
  '6': '/admin/approvals?status=incomplete',
  '7': '/admin/vendors',
};

const LegacyTabRedirect = ({ children }) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const tab = searchParams.get('tab');
  if (tab != null && LEGACY_TAB_ROUTES[tab] && location.pathname === '/admin') {
    return <Navigate to={LEGACY_TAB_ROUTES[tab]} replace />;
  }
  return children;
};

const AppRoutes = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<VendorOnboarding />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/setup-password" element={<SetupPassword />} />
        <Route path="/vendors" element={<VendorsList />} />
        <Route path="/exhibitor-info" element={<ExhibitorInfo />} />
        <Route path="/visitors" element={<Visitors />} />
        <Route path="/directions" element={<Directions />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/vendor/dashboard"
          element={
            <ProtectedRoute>
              <VendorDashboard />
            </ProtectedRoute>
          }
        />
        <Route path="/vendor/onboarding" element={<VendorOnboarding />} />
        <Route
          path="/vendor/payment"
          element={
            <ProtectedRoute>
              <VendorPaymentPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/vendor/registration-success"
          element={<Navigate to="/vendor/dashboard" replace />}
        />
        <Route path="/map" element={<MapView />} />
        <Route
          path="/manage-map"
          element={
            <ProtectedRoute requireSuperAdmin>
              <Navigate to="/admin/map" replace />
            </ProtectedRoute>
          }
        />

        {/* Rendered outside the admin shell so the sidebar never prints. */}
        <Route
          path="/admin/final-registrations/print"
          element={
            <ProtectedRoute requireAdmin>
              <VendorPrintSheets />
            </ProtectedRoute>
          }
        />

        {/* Admin shell with persistent sidebar; all admin pages render inside. */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute requireAdmin>
              <LegacyTabRedirect>
                <AdminLayout />
              </LegacyTabRedirect>
            </ProtectedRoute>
          }
        >
          <Route index element={<HomePage />} />
          <Route path="approvals" element={<ApprovalsPage />} />
          <Route path="vendors" element={<VendorsPage />} />
          <Route path="categories" element={<CategoriesPage />} />
          <Route path="booths" element={<BoothsPage />} />
          <Route path="payments" element={<PaymentsPage />} />
          <Route path="final-registrations" element={<FinalRegistrations />} />
          <Route path="registration-flow" element={<RegistrationFlowPage />} />
          <Route path="vendors/:vendorId" element={<VendorDetailsPage />} />
          <Route path="payments/:paymentId" element={<PaymentDetailsPage />} />
          <Route
            path="users"
            element={
              <ProtectedRoute requireSuperAdmin>
                <UsersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="webhooks"
            element={
              <ProtectedRoute requireSuperAdmin>
                <WebhooksPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="festivals"
            element={
              <ProtectedRoute requireSuperAdmin>
                <FestivalsManager />
              </ProtectedRoute>
            }
          />
          <Route
            path="map"
            element={
              <ProtectedRoute requireSuperAdmin>
                <MapEditorPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="email-settings"
            element={
              <ProtectedRoute requireSuperAdmin>
                <EmailSettingsPage />
              </ProtectedRoute>
            }
          />
        </Route>

        <Route path="/vendor/confirmation" element={<VendorConfirmationPage />} />
      </Routes>
    </ThemeProvider>
  );
};

export default AppRoutes;
